import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { tokens } from "../../../theme";

export type BreakdownPart = {
  label: string;
  rate?: number;
  amount?: number;
};

export type InvoiceCommissionRow = {
  key: string;
  label: string;
  rate: number;
  rateBreakdown?: BreakdownPart[];
  rateBreakdownOnly?: boolean;
  expectedTotal: number;
  expectedBreakdown?: BreakdownPart[];
  receivedTotal: number;
};

type InvoiceCommissionSummaryProps = {
  v3Rows: InvoiceCommissionRow[];
  payableRows: InvoiceCommissionRow[];
  netRows: InvoiceCommissionRow[];
};

const formatAmount = (amount: number) =>
  `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatRate = (rate: number) =>
  `${rate.toFixed(rate % 1 === 0 ? 0 : 2)}%`;

const HEADER_COLUMNS = "minmax(0, 1.3fr) minmax(190px, 1fr) minmax(190px, 1fr)";
const METRIC_COLUMNS = "108px 12px minmax(0, 1fr)";

const CollapsedMetric = ({
  amount,
  label,
  accentColor,
}: {
  amount: number;
  label: string;
  accentColor: string;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: METRIC_COLUMNS,
      alignItems: "baseline",
      minWidth: 0,
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: "0.9rem",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: accentColor,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {formatAmount(amount)}
    </Typography>
    <Typography
      component="span"
      sx={{ color: "text.disabled", fontSize: "0.9rem", textAlign: "center" }}
    >
      /
    </Typography>
    <Typography
      component="span"
      sx={{
        color: "text.secondary",
        fontSize: "0.9rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const RateCell = ({
  rate,
  rateBreakdown,
  rateBreakdownOnly,
}: {
  rate: number;
  rateBreakdown?: BreakdownPart[];
  rateBreakdownOnly?: boolean;
}) => {
  if (rateBreakdownOnly && rateBreakdown?.length) {
    // CS-2104: stack each payment rate on its own line so P1/P2 do not wrap mid-pair.
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.35,
          width: "100%",
        }}
      >
        {rateBreakdown.map((part, index) => (
          <Box
            key={`${part.label}-${index}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: 0.75,
              alignItems: "baseline",
            }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ color: "text.secondary", lineHeight: 1.3 }}
            >
              {part.label}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                color: "text.primary",
                lineHeight: 1.3,
              }}
            >
              {formatRate(part.rate ?? 0)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "inherit" }}
    >
      {formatRate(rate ?? 0)}
    </Typography>
  );
};

const AmountCell = ({
  total,
  breakdown,
  amountColor,
}: {
  total: number;
  breakdown?: BreakdownPart[];
  amountColor: string;
}) => (
  <Box sx={{ textAlign: "right", width: "100%" }}>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color: amountColor,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.3,
      }}
    >
      {formatAmount(total)}
    </Typography>
    {breakdown && breakdown.length > 0 && (
      <Box
        sx={{
          mt: 0.35,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.25,
        }}
      >
        {breakdown.map((part, index) => (
          <Box
            key={`${part.label}-${index}`}
            sx={{
              display: "flex",
              alignItems: "baseline",
              columnGap: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", lineHeight: 1.3 }}
            >
              {part.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.3,
              }}
            >
              {formatAmount(part.amount ?? 0)}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

const MobileRows = ({
  rows,
  expectedLabel,
  receivedLabel,
  lineColor,
}: {
  rows: InvoiceCommissionRow[];
  expectedLabel: string;
  receivedLabel: string;
  lineColor: string;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <>
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            p: 1.5,
            mb: 1,
            borderRadius: 2,
            border: `1px solid ${colors.grey[700]}`,
            backgroundColor:
              theme.palette.mode === "dark" ? "#0b1520" : colors.grey[100],
            "&:last-child": { mb: 0 },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
            {row.label}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Rate
              </Typography>
              <RateCell
                rate={row.rate}
                rateBreakdown={row.rateBreakdown}
                rateBreakdownOnly={row.rateBreakdownOnly}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {expectedLabel}
              </Typography>
              <AmountCell
                total={row.expectedTotal}
                breakdown={row.expectedBreakdown}
                amountColor={lineColor}
              />
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="caption" color="text.secondary">
                {receivedLabel}
              </Typography>
              <AmountCell total={row.receivedTotal} amountColor={lineColor} />
            </Box>
          </Box>
        </Box>
      ))}
    </>
  );
};

const DESKTOP_COLUMNS = "minmax(0, 1.6fr) minmax(88px, max-content) minmax(0, 1fr) minmax(0, 1fr)";

const DesktopTable = ({
  rows,
  expectedLabel,
  receivedLabel,
  accentColor,
  lineColor,
}: {
  rows: InvoiceCommissionRow[];
  expectedLabel: string;
  receivedLabel: string;
  accentColor: string;
  lineColor: string;
}) => {
  const theme = useTheme();
  const headerBg = theme.palette.mode === "dark" ? "#272c3452" : "#e4e4e454";
  const stripeBg = theme.palette.mode === "dark" ? "#0d1824" : "#fafbfc";
  const cellSx = {
    px: 2,
    py: 1.1,
    minWidth: 0,
    display: "flex",
    alignItems: "flex-start",
  };

  return (
    <Box sx={{ overflow: "hidden", borderRadius: 1.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: DESKTOP_COLUMNS,
          alignItems: "center",
          backgroundColor: headerBg,
        }}
      >
        <Box sx={cellSx}>
          <Typography fontWeight="bold" sx={{ color: accentColor }}>
            Type
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography fontWeight="bold" sx={{ color: accentColor }}>
            Rate
          </Typography>
        </Box>
        <Box sx={{ ...cellSx, justifyContent: "flex-end" }}>
          <Typography fontWeight="bold" sx={{ color: accentColor }}>
            {expectedLabel}
          </Typography>
        </Box>
        <Box sx={{ ...cellSx, justifyContent: "flex-end" }}>
          <Typography fontWeight="bold" sx={{ color: accentColor }}>
            {receivedLabel}
          </Typography>
        </Box>
      </Box>
      {rows.map((row, index) => (
        <Box
          key={row.key}
          sx={{
            display: "grid",
            gridTemplateColumns: DESKTOP_COLUMNS,
            alignItems: "start",
            backgroundColor: index % 2 === 0 ? stripeBg : "transparent",
          }}
        >
          <Box sx={cellSx}>
            <Typography sx={{ fontWeight: 600, color: lineColor }}>
              {row.label}
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <RateCell
              rate={row.rate}
              rateBreakdown={row.rateBreakdown}
              rateBreakdownOnly={row.rateBreakdownOnly}
            />
          </Box>
          <Box sx={{ ...cellSx, justifyContent: "flex-end" }}>
            <AmountCell
              total={row.expectedTotal}
              breakdown={row.expectedBreakdown}
              amountColor={lineColor}
            />
          </Box>
          <Box sx={{ ...cellSx, justifyContent: "flex-end" }}>
            <AmountCell total={row.receivedTotal} amountColor={lineColor} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const SummarySection = ({
  title,
  accentColor,
  rows,
  expectedLabel,
  receivedLabel,
  isMobile,
  expanded,
  onToggle,
}: {
  title: string;
  accentColor: string;
  rows: InvoiceCommissionRow[];
  expectedLabel: string;
  receivedLabel: string;
  isMobile: boolean;
  expanded: boolean;
  onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const lineColor = theme.palette.mode === "dark" ? "#fff" : "#1e293b";

  if (!rows.length) return null;

  const expectedSum = rows.reduce((sum, row) => sum + (row.expectedTotal || 0), 0);
  const receivedSum = rows.reduce((sum, row) => sum + (row.receivedTotal || 0), 0);

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      square
      sx={{
        backgroundColor: colors.grey[800] || colors.grey[900],
        borderRadius: 2,
        mb: 1,
        border: `1px solid ${colors.grey[700]}`,
        "&:before": { display: "none" },
        "&:last-of-type": { mb: 0 },
        "&:hover": {
          backgroundColor: colors.grey[700],
        },
      }}
    >
      <AccordionSummary
        expandIcon={expanded ? <RemoveIcon /> : <AddIcon />}
        aria-label={title}
        sx={{
          backgroundColor: "transparent",
          borderRadius: 2,
          px: { xs: 1.5, sm: 2 },
          py: 0.5,
          minHeight: "48px",
          "&.Mui-expanded": {
            minHeight: "48px",
          },
          "& .MuiAccordionSummary-content": {
            my: 0.5,
            alignItems: "center",
            overflow: "visible",
            width: "100%",
            mr: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: expanded ? "1fr" : HEADER_COLUMNS,
            alignItems: "center",
            width: "100%",
            columnGap: 2,
            minWidth: 0,
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ color: accentColor, minWidth: 0 }}>
            {title}
          </Typography>
          {!expanded && (
            <>
              <CollapsedMetric
                amount={expectedSum}
                label={expectedLabel}
                accentColor={accentColor}
              />
              <CollapsedMetric
                amount={receivedSum}
                label={receivedLabel}
                accentColor={accentColor}
              />
            </>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 1.5 }}>
        {isMobile ? (
          <MobileRows
            rows={rows}
            expectedLabel={expectedLabel}
            receivedLabel={receivedLabel}
            lineColor={lineColor}
          />
        ) : (
          <DesktopTable
            rows={rows}
            expectedLabel={expectedLabel}
            receivedLabel={receivedLabel}
            accentColor={accentColor}
            lineColor={lineColor}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const InvoiceCommissionSummary: React.FC<InvoiceCommissionSummaryProps> = ({
  v3Rows,
  payableRows,
  netRows,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showNet = netRows.length > 0 && v3Rows.length > 0;
  const hasData = v3Rows.length > 0 || payableRows.length > 0 || showNet;
  // Open by default, same as payment accordions.
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    v3: true,
    payable: true,
    net: true,
  });

  const toggleSection =
    (id: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSections((prev) => ({ ...prev, [id]: isExpanded }));
    };

  if (!hasData) return null;

  return (
    <Box>
      {v3Rows.length > 0 && (
        <SummarySection
          title="Service Fee Receivable"
          accentColor="#22c55e"
          rows={v3Rows}
          expectedLabel="Receivable"
          receivedLabel="Received"
          isMobile={isMobile}
          expanded={!!expandedSections.v3}
          onToggle={toggleSection("v3")}
        />
      )}
      {payableRows.length > 0 && (
        <SummarySection
          title="Service Fee Payable"
          accentColor={colors.info[300]}
          rows={payableRows}
          expectedLabel="Payable"
          receivedLabel="Paid"
          isMobile={isMobile}
          expanded={!!expandedSections.payable}
          onToggle={toggleSection("payable")}
        />
      )}
      {showNet && (
        <SummarySection
          title="Net Service Fee Receivable"
          accentColor={colors.info[300]}
          rows={netRows}
          expectedLabel="Net Receivable"
          receivedLabel="Net Received"
          isMobile={isMobile}
          expanded={!!expandedSections.net}
          onToggle={toggleSection("net")}
        />
      )}
    </Box>
  );
};

export default InvoiceCommissionSummary;
